/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Grid, IconButton, PageGridContainer, Typography, breakpoints, useTheme } from '@coursera/cds-core';

import Carousel, { ArrowButton } from 'bundles/design-system/components/Carousel';
import type { CarouselSettings as CarouselSettingsType } from 'bundles/design-system/components/types';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import useIsTablet from 'bundles/unified-description-page-common/hooks/useIsTablet';

import _t from 'i18n!nls/xdp';

type Props = {
  title: string;
  anchorId?: string;
  testimonialItems: Array<React.ReactNode>;
  isInsideContentPanel?: boolean;
  slidesToShow?: number;
  spacing?: number;
};

const styles = {
  carousel: (isInsideContentPanel?: boolean) => css`
    padding: ${isInsideContentPanel
      ? 'var(--cds-spacing-100) var(--cds-spacing-200) 0 var(--cds-spacing-200)'
      : `var(--cds-spacing-200) var(--cds-spacing-400)`};

    // This chain of selectors is to make sure the testimonials inside
    // the carousel component have the same height.
    &,
    & > div,
    .rc-Carousel,
    .slick-slider,
    .slick-list,
    .slick-track,
    .slick-slide,
    .slick-slide > div {
      height: 100%;
    }

    .slick-prev {
      left: ${isInsideContentPanel ? `calc(var(--cds-spacing-200) * -1)` : ''};
    }

    .slick-next {
      right: ${isInsideContentPanel ? `calc(var(--cds-spacing-200) * -1)` : ''};
    }

    [dir='rtl'] {
      .slick-prev {
        right: -16px;
      }

      .slick-next {
        left: -16px;
      }
    }
  `,
  testimonial: (isInsideContentPanel?: boolean) => css`
    height: 100%;
    padding: 0 var(--cds-spacing-200);
    ${breakpoints.down('md')} {
      padding: 0 var(--cds-spacing-100);

      // Avoids overlap with carousel arrows on mobile/tablet
      > div {
        padding: ${isInsideContentPanel ? `var(--cds-spacing-200) var(--cds-spacing-600)` : ''};
      }
    }
  `,
  contentPanelContainer: css`
    padding: 0 !important;
    margin-bottom: var(--cds-spacing-300);
  `,
  carouselButtonIconWrap: css`
    position: absolute;
    top: 40%;
    height: auto !important;
    z-index: 1;
  `,
};

const TestimonialsModule = ({
  title,
  anchorId,
  testimonialItems,
  isInsideContentPanel,
  slidesToShow = 2,
  spacing,
}: Props) => {
  const theme = useTheme();
  const isTablet = useIsTablet();
  const testimonialSingularOrPlural = slidesToShow > 1 ? _t('testimonials') : _t('testimonial');
  const isRTL = isRightToLeft(_t.getLocale());
  const prevArrowLabel = _t('Go to previous #{testimonialSingularOrPlural}', {
    testimonialSingularOrPlural,
  });
  const nextArrowLabel = _t('Go to next #{testimonialSingularOrPlural}', {
    testimonialSingularOrPlural,
  });

  const carouselSettings: CarouselSettingsType = useMemo(
    () => ({
      slidesToShow,
      slidesToScroll: slidesToShow,
      responsive: [
        {
          breakpoint: theme.breakpoints.values.sm,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            variableWidth: false,
            dots: true,
          },
        },
        {
          breakpoint: theme.breakpoints.values.md,
          settings: {
            slidesToShow,
            slidesToScroll: slidesToShow,
            variableWidth: false,
            dots: true,
          },
        },
      ],
      isFluid: true,
      fade: false,
      dots: true,
    }),
    [theme, slidesToShow]
  );

  if (testimonialItems.length === 0) {
    return null;
  }

  return (
    <Section anchorId={anchorId} spacing={spacing}>
      <TrackedDiv
        trackingName="user_testimonial"
        withVisibilityTracking={true}
        requireFullyVisible={false}
        trackClicks={false}
      >
        <PageGridContainer css={isInsideContentPanel ? styles.contentPanelContainer : ''}>
          <Grid item xs={12}>
            <Typography variant="h1semibold" component="h2">
              {title}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {/* Show 4 testimonials on the page. The carousel comes in when viewport gets down to tablet view. */}
            {!isTablet && slidesToShow > 1 ? (
              <Grid container spacing={32}>
                {testimonialItems.slice(0, 4).map((testimonial, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid item md={3} key={index}>
                    {testimonial}
                  </Grid>
                ))}
              </Grid>
            ) : (
              <div css={styles.carousel(isInsideContentPanel)}>
                <Carousel
                  settings={{
                    ...carouselSettings,
                    prevArrow: isInsideContentPanel ? (
                      <div css={styles.carouselButtonIconWrap} aria-label={isRTL ? nextArrowLabel : prevArrowLabel}>
                        <IconButton intent={isRTL ? 'next' : 'previous'} variant="secondary" />
                      </div>
                    ) : (
                      <ArrowButton
                        isArrowNext={isRTL}
                        isArrowRight={false}
                        label={isRTL ? nextArrowLabel : prevArrowLabel}
                      />
                    ),
                    nextArrow: isInsideContentPanel ? (
                      <div css={styles.carouselButtonIconWrap} aria-label={isRTL ? prevArrowLabel : nextArrowLabel}>
                        <IconButton intent={isRTL ? 'previous' : 'next'} variant="secondary" />
                      </div>
                    ) : (
                      <ArrowButton
                        isArrowNext={!isRTL}
                        isArrowRight={true}
                        label={isRTL ? prevArrowLabel : nextArrowLabel}
                      />
                    ),
                  }}
                >
                  {testimonialItems.slice(0, 4).map((testimonial, index) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      css={styles.testimonial(isInsideContentPanel)}
                    >
                      {testimonial}
                    </div>
                  ))}
                </Carousel>
              </div>
            )}
          </Grid>
        </PageGridContainer>
      </TrackedDiv>
    </Section>
  );
};

export default TestimonialsModule;
